import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { PreviewPlanChangeResponse, SubscriptionsService } from '../../../../services/subscriptions'
import { ActivatedRoute, Router } from '@angular/router'
import { eps } from '../../../../services/url.service'
import { Store } from '@ngrx/store'
import { AuthState, selectSubscription, selectUser } from '../../../../store/auth/auth.state'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  min,
  Observable,
  retry,
  switchMap,
  take,
  tap,
  timer
} from 'rxjs'
import { PaymentsService } from '../../../../services/payments.service'
import { ToastrService } from 'ngx-toastr'
import { RazorpayService } from '../../../../services/razorpay.service'
import { UserService } from '../../../../services/user.service'

@Component({
  selector: 'app-plan-change-summary',
  imports: [CommonModule, CapitalizePipe],
  templateUrl: './plan-change-summary.component.html',
  styleUrl: './plan-change-summary.component.scss'
})
export class PlanChangeSummaryComponent implements OnInit {
  private store = inject(Store)
  private subscriptionsService = inject(SubscriptionsService)
  private paymentsService = inject(PaymentsService)
  private razorpayService = inject(RazorpayService)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private toastr = inject(ToastrService)
  private userService = inject(UserService)

  selectedPlanId!: string
  currentSubscriptionId!: string
  businessId!: string
  isProcessingPayment = false
  planChangeSummary$!: Observable<PreviewPlanChangeResponse>
  user: AuthState['user'] | null = null
  private userInputLicences$ = new BehaviorSubject<number | null>(null)

  ngOnInit(): void {
    const selectedPlanId = this.route.snapshot.paramMap.get('id')
    if (!selectedPlanId) this.router.navigate([eps.hrm.upgradePlanPath])
    else this.selectedPlanId = selectedPlanId

    //const currentSubscriptionId$ = this.store.select(selectSubscription).pipe(map(sub => sub?.id))
    //const businessId$ = this.store.select(selectUser).pipe(map(user => user?.businessId))

    //this.planChangeSummary$ = combineLatest([currentSubscriptionId$, businessId$]).pipe(
    //  filter(([subId, busId]) => !!subId && !!busId),
    //  take(1),
    //  tap(([subId, busId]) => {
    //    this.currentSubscriptionId = subId!
    //    this.businessId = busId!
    //  }),
    //  switchMap(([subId, busId]) => {
    //    return this.subscriptionsService.previewPlanChange(subId!, this.selectedPlanId, busId!)
    //  })
    //)
    const initialData$ = combineLatest([
      this.store.select(selectSubscription).pipe(map(sub => sub?.id)),
      this.store.select(selectUser).pipe(map(user => user?.businessId))
    ]).pipe(
      filter(([subId, busId]) => !!subId && !!busId),
      take(1),
      tap(([subId, busId]) => {
        this.currentSubscriptionId = subId!
        this.businessId = busId!
      })
    )

    // The main reactive stream. It re-triggers whenever the license count changes.
    this.planChangeSummary$ = combineLatest([
      initialData$,
      this.userInputLicences$.pipe(debounceTime(300), distinctUntilChanged())
    ]).pipe(
      switchMap(([[subId, busId], licencesCount]) => {
        return this.subscriptionsService.previewPlanChange(
          subId!,
          this.selectedPlanId,
          busId!,
          licencesCount ?? undefined
        )
      }),
      tap(summary => {
        if (this.userInputLicences$.value === null) {
          this.userInputLicences$.next(summary.newPlan.requestedLicences)
        }
      })
    )

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap(user => {
          this.user = user
        })
      )
      .subscribe()
  }

  onLicenceChange(newCount: number, minLicences: number): void {
    const validatedCount = Math.max(minLicences, Number(newCount) || minLicences)
    this.userInputLicences$.next(validatedCount)
  }

  //getPaymentLink() {
  //  this.planChangeSummary$.pipe(take(1)).subscribe(summary => {
  //    const finalLicencesCount = summary.newPlan.requestedLicences
  //    this.paymentsService
  //      .getPaymentLink(this.currentSubscriptionId, this.selectedPlanId, finalLicencesCount)
  //      .pipe(take(1))
  //      .subscribe(r => {
  //        if (r.message && !r.paymentNeeded && !r.redirectUrl) {
  //          this.toastr.success(r.message)
  //          this.router.navigate([eps.planAndBilling.planPath])
  //        }
  //        if (r.paymentNeeded && r.redirectUrl) window.location.href = r.redirectUrl
  //      })
  //  })
  //}

  changePlan(): void {
    if (this.isProcessingPayment) return
    this.isProcessingPayment = true

    this.planChangeSummary$.pipe(take(1)).subscribe(summary => {
      const finalLicencesCount = summary.newPlan.requestedLicences

      this.paymentsService
        .initiatePlanChange(this.currentSubscriptionId, this.selectedPlanId, finalLicencesCount)
        .pipe(take(1))
        .subscribe({
          next: response => {
            this.showRazorpayCheckout(response, summary)
          },
          error: err => {
            this.isProcessingPayment = false
            this.toastr.error(err.error?.message || 'Failed to initiate payment. Please try again.')
          }
        })
    })
  }

  private showRazorpayCheckout(initiationResponse: any, summary: any): void {
    const newTotalLicences = summary.newPlan.requestedLicences
    const newPlanId = summary.newPlan.id
    this.razorpayService
      .load()
      .then(() => {
        const options = {
          key: initiationResponse.key,
          order_id: initiationResponse.orderId,

          name: 'AVR HRMS',
          description: `Change Plan`,

          handler: (response: any) => {
            // Success Callback: The Webhook will handle the actual backend update.
            // We just notify the user and redirect.
            console.log('Razorpay payment success:', response)
            this.toastr.success('Payment successful! Plan is being changed.')
            this.fetchAndSaveCurrentSubscription(this.businessId)
            this.router.navigate([eps.planAndBilling.planPath])
          },

          prefill: {
            // Optional: Prefill user details
            name: this.user?.name ?? '',
            email: this.user?.email ?? '',
            contact: this.user?.mobile ?? ''
          },

          notes: {
            context: 'PLAN_CHANGE',
            newPlanId,
            newTotalLicences
          },

          modal: {
            ondismiss: () => {
              this.isProcessingPayment = false
              this.toastr.info('Payment cancelled.')
            }
          },

          theme: {
            color: '#3399cc'
          }
        }

        const rzp = new window.Razorpay(options)
        rzp.on('payment.failed', (response: any) => {
          this.isProcessingPayment = false
          this.toastr.error(response.error.description || 'Payment failed')
        })
        rzp.open()
      })
      .catch(error => {
        console.error('Failed to load Razorpay script', error)
        this.toastr.error('Could not connect to payment gateway.')
        this.isProcessingPayment = false
      })
  }

  private fetchAndSaveCurrentSubscription(businessId: string): void {
    let attempt = 0
    const maxAttempts = 5

    this.userService
      .getCurrentSubscription(businessId)
      .pipe(
        retry({
          count: maxAttempts - 1,
          delay: (error, retryCount) => {
            attempt = retryCount

            console.warn(
              `Failed to fetch current subscription. Retry ${retryCount}/${maxAttempts - 1} after 1 minute.`,
              error
            )

            return timer(60000)
          }
        })
      )
      .subscribe({
        next: subscription => {
          console.log('Current subscription fetched successfully:', subscription)

          localStorage.setItem('subscription', JSON.stringify(subscription))

          this.toastr.success('Subscription updated successfully.')

          // Reload only after successful API response
          setTimeout(() => {
            window.location.reload()
          }, 500)
          this.router.navigate([eps.planAndBilling.billingPath])
        },

        error: err => {
          console.error(`Failed to fetch current subscription after ${maxAttempts} attempts.`, err)

          this.isProcessingPayment = false

          this.toastr.error(
            'Subscription payment was successful, but we could not update your subscription details. Please try again later.'
          )
        }
      })
  }
}
